import React, { useState, useEffect } from 'react';
import axios from 'axios';

const EMPTY_FORM = {
  client_id: '', broker_id: '', scheduled_date: new Date().toISOString().split('T')[0],
  scheduled_time: '', type: 'call', notes: '', remarks: '',
  next_followup_date: '', status: 'pending'
};

const Followups = ({ apiBase }) => {
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFollowup, setEditingFollowup] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [clients, setClients] = useState([]);
  const [channelPartners, setCPs] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [revealedPhones, setRevealedPhones] = useState({});
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completingFollowup, setCompletingFollowup] = useState(null);
  const [completeForm, setCompleteForm] = useState({ remarks: '', notes: '', next_followup_date: '', outcome: 'positive' });

  useEffect(() => {
    fetchFollowups();
    axios.get(`${apiBase}/clients/list`).then(r => setClients(r.data || [])).catch(() => {});
    axios.get(`${apiBase}/channel-partners`).then(r => setCPs(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchFollowups(); }, [filterStatus, filterType]);

  const fetchFollowups = async () => {
    try {
      let params = [];
      if (filterStatus) params.push(`status=${filterStatus}`);
      if (filterType) params.push(`type=${filterType}`);
      const qs = params.length > 0 ? '?' + params.join('&') : '';
      const response = await axios.get(`${apiBase}/followups${qs}`);
      setFollowups(response.data);
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
    } finally {
      setLoading(false);
    }
  };

  const openNewFollowup = () => {
    setEditingFollowup(null);
    setFormData({ ...EMPTY_FORM });
    setShowModal(true);
  };

  const handleEdit = (f) => {
    setEditingFollowup(f);
    setFormData({
      client_id: f.client_id || '', broker_id: f.broker_id || '',
      scheduled_date: f.scheduled_date ? f.scheduled_date.split('T')[0] : '',
      scheduled_time: f.scheduled_time || '', type: f.type || 'call',
      notes: f.notes || '', remarks: f.remarks || '',
      next_followup_date: f.next_followup_date ? f.next_followup_date.split('T')[0] : '',
      status: f.status || 'pending'
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFollowup) {
        await axios.put(`${apiBase}/followups/${editingFollowup.id}`, formData);
      } else {
        await axios.post(`${apiBase}/followups`, formData);
      }
      setShowModal(false);
      setEditingFollowup(null);
      setFormData({ ...EMPTY_FORM });
      fetchFollowups();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleComplete = (f) => {
    setCompletingFollowup(f);
    setCompleteForm({ remarks: f.remarks || '', notes: '', next_followup_date: '', outcome: 'positive' });
    setShowCompleteModal(true);
  };

  const submitComplete = async (e) => {
    e.preventDefault();
    if (!completingFollowup) return;
    try {
      const payload = {
        remarks: completeForm.remarks,
        notes: [completeForm.notes, completeForm.outcome ? `Outcome: ${completeForm.outcome}` : ''].filter(Boolean).join(' | '),
        next_followup_date: completeForm.next_followup_date || null
      };
      await axios.post(`${apiBase}/followups/${completingFollowup.id}/complete`, payload);
      setShowCompleteModal(false);
      setCompletingFollowup(null);
      fetchFollowups();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this follow-up? This cannot be undone.')) return;
    try {
      await axios.delete(`${apiBase}/followups/${id}`);
      fetchFollowups();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const toggleRevealPhone = (id) => {
    setRevealedPhones(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const maskPhone = (phone) => {
    if (!phone) return '-';
    return phone.slice(0, 4) + '****' + phone.slice(-2);
  };

  if (loading) return <div className="loading">Loading follow-ups...</div>;

  const pendingCount = followups.filter(f => f.status === 'pending').length;
  const completedCount = followups.filter(f => f.status === 'completed').length;
  const todayStr = new Date().toISOString().split('T')[0];
  const overdueCount = followups.filter(f => f.status === 'pending' && f.scheduled_date && f.scheduled_date.split('T')[0] < todayStr).length;

  const getTypeIcon = (type) => {
    const icons = { call: '☎️', whatsapp: '💬', email: '✉️', personal_meeting: '🤝', calling: '📞', remarks: '📝' };
    return icons[type] || '📌';
  };

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'Total', value: followups.length, color: '#3b82f6', bg: '#eff6ff' },
          { label: 'Pending', value: pendingCount, color: '#f59e0b', bg: '#fffbeb' },
          { label: 'Completed', value: completedCount, color: '#10b981', bg: '#ecfdf5' },
          { label: 'Overdue', value: overdueCount, color: '#ef4444', bg: '#fef2f2' },
        ].map((s, i) => (
          <div key={i} style={{ background: s.bg, border: `1px solid ${s.color}30`, borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: '800', color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: s.color, opacity: 0.8 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <h2 className="card-title" style={{ marginBottom: 0 }}>Client Follow-ups</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select className="form-input" style={{ width: 'auto', padding: '6px 10px', fontSize: '13px' }}
              value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select className="form-input" style={{ width: 'auto', padding: '6px 10px', fontSize: '13px' }}
              value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="">All Types</option>
              <option value="call">Call</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="email">Email</option>
              <option value="personal_meeting">Meeting</option>
              <option value="remarks">Remarks</option>
            </select>
            <button className="btn btn-primary" onClick={openNewFollowup} style={{ whiteSpace: 'nowrap' }}>+ New Follow-up</button>
          </div>
        </div>
        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Phone</th>
                <th>Type</th>
                <th>Scheduled</th>
                <th>Who Called</th>
                <th>Outcome / Remarks</th>
                <th>Broker</th>
                <th>Next Follow-up</th>
                <th>Status</th>
                <th style={{ minWidth: '150px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {followups.length > 0 ? followups.map((f) => {
                const isOverdue = f.status === 'pending' && f.scheduled_date && f.scheduled_date.split('T')[0] < todayStr;
                return (
                  <tr key={f.id} style={{ background: isOverdue ? '#fef2f220' : undefined }}>
                    <td>
                      <strong>{f.client_name || '-'}</strong>
                      {f.client_email && <div style={{ fontSize: '11px', color: '#64748b' }}>{f.client_email}</div>}
                    </td>
                    <td>
                      {f.client_phone ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                            {revealedPhones[f.id] ? f.client_phone : maskPhone(f.client_phone)}
                          </span>
                          <button onClick={() => toggleRevealPhone(f.id)} title={revealedPhones[f.id] ? 'Hide' : 'Reveal'}
                            style={{ padding: '2px 6px', borderRadius: '4px', border: '1px solid #d1d5db', background: '#f8fafc', cursor: 'pointer', fontSize: '11px' }}>
                            {revealedPhones[f.id] ? '🙈' : '👁️'}
                          </button>
                          {revealedPhones[f.id] && (
                            <a href={`tel:${f.client_phone}`} title="Call" style={{ fontSize: '14px', textDecoration: 'none' }}>📞</a>
                          )}
                        </div>
                      ) : '-'}
                    </td>
                    <td>
                      <span className="badge badge-info" style={{ fontSize: '11px' }}>
                        {getTypeIcon(f.type)} {f.type || 'call'}
                      </span>
                    </td>
                    <td>
                      <div>{f.scheduled_date ? new Date(f.scheduled_date).toLocaleDateString('en-IN') : '-'}</div>
                      {f.scheduled_time && <div style={{ fontSize: '11px', color: '#64748b' }}>{f.scheduled_time}</div>}
                      {isOverdue && <span style={{ fontSize: '10px', color: '#ef4444', fontWeight: 600 }}>⚠️ OVERDUE</span>}
                    </td>
                    <td>
                      <div>{f.created_by_name || '-'}</div>
                      {f.completed_by_name && <div style={{ fontSize: '11px', color: '#10b981' }}>✅ {f.completed_by_name}</div>}
                    </td>
                    <td style={{ maxWidth: '200px' }}>
                      {f.remarks && <div style={{ fontSize: '12px', fontWeight: 500, color: '#1e293b', marginBottom: '2px' }}>{f.remarks}</div>}
                      {f.notes && f.notes !== f.remarks && <div style={{ fontSize: '11px', color: '#64748b' }}>{f.notes.length > 80 ? f.notes.substring(0, 80) + '...' : f.notes}</div>}
                      {f.outcome && <div style={{ fontSize: '11px', color: '#3b82f6', fontStyle: 'italic' }}>{f.outcome}</div>}
                      {!f.remarks && !f.notes && !f.outcome && <span style={{ color: '#d1d5db' }}>-</span>}
                    </td>
                    <td>
                      {f.broker_name ? (
                        <div>
                          <span className="badge badge-info" style={{ fontSize: '11px' }}>{f.broker_name}</span>
                          {f.broker_phone && revealedPhones[`b_${f.id}`] ? (
                            <div style={{ fontSize: '11px', marginTop: 2 }}>{f.broker_phone}</div>
                          ) : null}
                        </div>
                      ) : <span style={{ color: '#10b981', fontWeight: 500, fontSize: '12px' }}>Direct</span>}
                    </td>
                    <td>
                      {f.next_followup_date ? (
                        <span style={{ fontSize: '12px', fontWeight: 500 }}>{new Date(f.next_followup_date).toLocaleDateString('en-IN')}</span>
                      ) : '-'}
                    </td>
                    <td>
                      <span className={`badge badge-${getStatusColor(f.status)}`}>{f.status}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {f.status === 'pending' && (
                          <button onClick={() => handleComplete(f)} title="Mark Complete"
                            style={{ padding: '3px 8px', borderRadius: '6px', border: '1px solid #86efac', background: '#f0fdf4', color: '#16a34a', cursor: 'pointer', fontSize: '11px', fontWeight: 500 }}>
                            ✅ Done
                          </button>
                        )}
                        <button onClick={() => handleEdit(f)} title="Edit"
                          style={{ padding: '3px 8px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#f8fafc', color: '#1e293b', cursor: 'pointer', fontSize: '11px', fontWeight: 500 }}>
                          ✏️
                        </button>
                        <button onClick={() => handleDelete(f.id)} title="Delete"
                          style={{ padding: '3px 8px', borderRadius: '6px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: '11px', fontWeight: 500 }}>
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr><td colSpan="10" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No follow-ups found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3 className="modal-title">{editingFollowup ? `Edit Follow-up #${editingFollowup.id}` : 'New Follow-up'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>{'×'}</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Client *</label>
                  <select className="form-input" required value={formData.client_id} onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}>
                    <option value="">-- Select Client --</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name} {c.phone ? `(${c.phone})` : ''}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select className="form-input" value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                    <option value="call">{'☎️'} Call</option>
                    <option value="whatsapp">{'💬'} WhatsApp</option>
                    <option value="email">{'✉️'} Email</option>
                    <option value="personal_meeting">{'🤝'} Personal Meeting</option>
                    <option value="remarks">{'📝'} Remarks Only</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Scheduled Date</label>
                  <input type="date" className="form-input" value={formData.scheduled_date} onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Scheduled Time</label>
                  <input type="time" className="form-input" value={formData.scheduled_time} onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Broker / CP (if applicable)</label>
                  <select className="form-input" value={formData.broker_id} onChange={(e) => setFormData({ ...formData, broker_id: e.target.value })}>
                    <option value="">Direct (No Broker)</option>
                    {channelPartners.map(cp => <option key={cp.id} value={cp.id}>{cp.name} {cp.company_name ? `(${cp.company_name})` : ''}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-input" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Next Follow-up Date</label>
                  <input type="date" className="form-input" value={formData.next_followup_date} onChange={(e) => setFormData({ ...formData, next_followup_date: e.target.value })} />
                </div>
              </div>
              <div className="form-group" style={{ marginTop: '12px' }}>
                <label className="form-label">What did the client say? (Remarks)</label>
                <textarea className="form-input" rows="2" value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} placeholder="Client's response, feedback..." />
              </div>
              <div className="form-group">
                <label className="form-label">Internal Notes</label>
                <textarea className="form-input" rows="2" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Internal observations, action items..." />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  {editingFollowup ? 'Update Follow-up' : 'Create Follow-up'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Complete Follow-up Modal */}
      {showCompleteModal && completingFollowup && (
        <div className="modal-overlay" onClick={() => setShowCompleteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px' }}>
            <div className="modal-header">
              <h3 className="modal-title">{'\u2705'} Complete Follow-up</h3>
              <button className="modal-close" onClick={() => setShowCompleteModal(false)}>{'\u00d7'}</button>
            </div>
            <div style={{ padding: '0 0 8px 0', fontSize: '13px', color: '#64748b' }}>
              <strong>{completingFollowup.client_name}</strong> — {completingFollowup.type} on {completingFollowup.scheduled_date ? new Date(completingFollowup.scheduled_date).toLocaleDateString('en-IN') : '-'}
            </div>
            <form onSubmit={submitComplete}>
              <div className="form-group">
                <label className="form-label" style={{ fontWeight: 600 }}>{'\ud83d\udcac'} What did the client say? *</label>
                <textarea className="form-input" rows="3" required value={completeForm.remarks}
                  onChange={(e) => setCompleteForm({ ...completeForm, remarks: e.target.value })}
                  placeholder="Client's response, feedback, concerns..." style={{ fontSize: '14px' }} />
              </div>
              <div className="form-group" style={{ marginTop: '10px' }}>
                <label className="form-label" style={{ fontWeight: 600 }}>{'\ud83d\uddd2\ufe0f'} Team Discussion Notes</label>
                <textarea className="form-input" rows="3" value={completeForm.notes}
                  onChange={(e) => setCompleteForm({ ...completeForm, notes: e.target.value })}
                  placeholder="Internal observations, action items, next steps..." style={{ fontSize: '14px' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '10px' }}>
                <div className="form-group">
                  <label className="form-label">Outcome</label>
                  <select className="form-input" value={completeForm.outcome} onChange={(e) => setCompleteForm({ ...completeForm, outcome: e.target.value })}>
                    <option value="positive">{'\ud83d\udc4d'} Positive</option>
                    <option value="neutral">{'\ud83d\ude10'} Neutral</option>
                    <option value="negative">{'\ud83d\udc4e'} Negative</option>
                    <option value="no_response">{'\ud83d\udcf5'} No Response</option>
                    <option value="callback">{'\ud83d\udcde'} Callback Requested</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Next Follow-up Date</label>
                  <input type="date" className="form-input" value={completeForm.next_followup_date}
                    onChange={(e) => setCompleteForm({ ...completeForm, next_followup_date: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1, background: '#16a34a', borderColor: '#16a34a' }}>
                  {'\u2705'} Mark as Completed
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowCompleteModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const getStatusColor = (status) => {
  const colors = { 'pending': 'warning', 'completed': 'success', 'cancelled': 'danger' };
  return colors[status] || 'info';
};

export default Followups;
